import React from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, Zap, Stethoscope, BookOpen, Globe, Droplets, HeartPulseIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
export function AboutSection() {
  const {
    t
  } = useLanguage();
  const credentials = [{
    icon: <GraduationCap className="w-6 h-6" data-id="element-17" />,
    title: t('about', 'cred1Title'),
    desc: t('about', 'cred1Desc')
  }, {
    icon: <Zap className="w-6 h-6" data-id="element-18" />,
    title: t('about', 'cred2Title'),
    desc: t('about', 'cred2Desc')
  }, {
    icon: <Stethoscope className="w-6 h-6" data-id="element-19" />,
    title: t('about', 'cred3Title'),
    desc: t('about', 'cred3Desc')
  }, {
    icon: <BookOpen className="w-6 h-6" data-id="element-20" />,
    title: t('about', 'cred4Title'),
    desc: t('about', 'cred4Desc')
  }, {
    icon: <Globe className="w-6 h-6" data-id="element-21" />,
    title: t('about', 'cred5Title'),
    desc: t('about', 'cred5Desc')
  }, {
    icon: <Droplets className="w-6 h-6" data-id="element-22" />,
    title: t('about', 'cred6Title'),
    desc: t('about', 'cred6Desc')
  }, {
    icon: <HeartPulseIcon className="w-6 h-6" data-id="element-23" />,
    title: t('about', 'cred7Title'),
    desc: t('about', 'cred7Desc')
  }];
  return <section id="sobre-lorena" className="py-24 bg-white" data-id="element-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-25">
        {/* Photo + Bio */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20" data-id="element-26">
          {/* Lorena's Photo */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.7
        }} className="relative flex justify-center" data-id="element-27">
            {/* Decorative gold frame offset */}
            <div className="absolute top-4 left-4 lg:top-6 lg:left-6 w-full h-full border-2 border-gold rounded-sm max-w-md" data-id="element-28"></div>
            <div className="relative z-10 max-w-md w-full overflow-hidden rounded-sm shadow-2xl" data-id="element-29">
              <img src="https://cdn.magicpatterns.com/uploads/2pUTXhXTpbpa3vv9sBixCX/lore_owner_2.png" alt="Lorena Hernández — Fundadora de Lorena Beauty House" className="w-full h-auto object-cover" data-id="element-30" />
              {/* Subtle gold gradient overlay at bottom */}
              <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/30 to-transparent" data-id="element-31"></div>
              <div className="absolute bottom-4 left-0 right-0 text-center" data-id="element-32">
                <p className="text-white font-serif text-lg font-medium tracking-wide drop-shadow-lg" data-id="element-33">
                  Lorena Hernández
                </p>
                <p className="text-gold-light text-sm font-light tracking-wider drop-shadow-lg" data-id="element-34">
                  {t('about', 'role')}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Bio Text */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-35">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-dark mb-6" data-id="element-36">
              {t('about', 'title')}
            </h2>
            <div className="w-16 h-1 bg-gold mb-8" data-id="element-37"></div>

            <div className="space-y-5 text-lg text-dark/80" data-id="element-38">
              <p data-id="element-39">{t('about', 'p1')}</p>
              <p data-id="element-40">
                {'Lleg\xF3 en 2017, dejando atr\xE1s 10 a\xF1os de carrera construida en la Rep\xFAblica Dominicana, dispuesta a comenzar desde cero en una ciudad que no regala nada.'}
              </p>
              <p data-id="element-41">
                {'Estudi\xF3 en Christine Valmy, una de las escuelas de est\xE9tica y maquillaje m\xE1s prestigiosas de Nueva York donde se gradu\xF3 con honores.'}
              </p>
              <p data-id="element-42">
                {'Se certific\xF3. Obtuvo su licencia. Y despu\xE9s de todo eso, la misma escuela donde fue alumna la invit\xF3 a ser instructora.'}
              </p>
              <p className="text-xl font-serif font-medium text-gold-dark pt-4" data-id="element-43">
                {t('about', 'p5')}
              </p>
            </div>
          </motion.div>
        </div>

        {/* Credentials Grid */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.6
      }} data-id="element-44">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-45">
            {credentials.map((cred, index) => <div key={index} className="bg-beige-light p-6 rounded-sm border border-gold/20 hover:border-gold transition-colors" data-id="element-46">
                <div className="text-gold mb-4" data-id="element-47">{cred.icon}</div>
                <h3 className="font-serif font-bold text-dark mb-2" data-id="element-48">
                  {cred.title}
                </h3>
                <p className="text-sm text-dark/70" data-id="element-49">{cred.desc}</p>
              </div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}